import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigate, useLocation } from 'react-router-dom';
import SimpleCampusFacilities from '../components/university/SimpleCampusFacilities';
import DocumentViewerModal from '../components/DocumentViewerModal';
import UniversityFAQManagement from '../components/university/UniversityFAQManagement';
import UniversityFeeStructureManagement from '../components/university/UniversityFeeStructureManagement';
import ApplicationManagement from '../components/university/ApplicationManagement';
import AdditionalAdmissionQuestions from '../components/university/AdditionalAdmissionQuestions';
import { API_ENDPOINTS, API_BASE_URL, getAuthHeaders } from '../config/api';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import { formatFeeDisplay, formatProgramDisplay, getDurationOptions } from '../utils/formatters';
import {
 UserCircleIcon,
 EnvelopeIcon,
 PhoneIcon,
 AcademicCapIcon,
 BuildingOfficeIcon,
 CalendarIcon,
 MapPinIcon,
 PencilIcon,
 TrashIcon,
 EyeIcon,
 PlusIcon,
 HomeIcon,
 IdentificationIcon,
 XMarkIcon,
 QuestionMarkCircleIcon,
 CurrencyDollarIcon
} from '@heroicons/react/24/outline';

// Helper to format merit cutoff display
const formatMeritCutoff = (program) => {
 if (!program) return null;
 const rawMin = program.last_year_cutoff_min ?? program.min_cutoff ?? program.minimum_merit ?? program.min;
 const rawMax = program.last_year_cutoff_max ?? program.max_cutoff ?? program.maximum_merit ?? program.max;
 const min = rawMin === '' || rawMin === null || rawMin === undefined ? null : Number(rawMin);
 const max = rawMax === '' || rawMax === null || rawMax === undefined ? null : Number(rawMax);
 const hasMin = typeof min === 'number' && !Number.isNaN(min);
 const hasMax = typeof max === 'number' && !Number.isNaN(max);
 if (!hasMin && !hasMax) return null;
 if (hasMin && hasMax) return `${min}% - ${max}%`;
 if (hasMin) return `${min}%`;
 return `${max}%`;
};
// Edit Program Modal Component
const EditProgramModal = React.memo(({ program, onClose, onSave, saving, degreeTypes, fieldsOfStudy }) => {
 const [formData, setFormData] = useState({
 program_name: (program && (program.name || program.program_name)) || '',
 program_fee: (program && (program.fee || program.program_fee)) || '',
 application_deadline: program && (program.deadline || program.application_deadline)
 ? (program.deadline || program.application_deadline).split?.('T')?.[0] || ''
 : '',
 duration: (program && (program.duration || program.program_duration)) || '',
 degree_type_id: (program && (program.degree_type_id || program.degree_type)) || '',
 field_of_study_id: (program && (program.field_of_study_id || program.field_of_study)) || '',
 description: (program && (program.description || program.program_description)) || '',
 last_year_cutoff_min: (program && (program.last_year_cutoff_min ?? program.min_cutoff ?? '')) ?? '',
 last_year_cutoff_max: (program && (program.last_year_cutoff_max ?? program.max_cutoff ?? '')) ?? ''
 });

 const handleInputChange = (field, value) => {
 setFormData(prev => ({ ...prev, [field]: value }));
 };

 const handleSubmit = () => {
 const payload = {
 ...formData,
 program_fee: formData.program_fee === '' ? null : (isNaN(Number(formData.program_fee)) ? formData.program_fee : Number(formData.program_fee)),
 last_year_cutoff_min: formData.last_year_cutoff_min === '' ? null : Number(formData.last_year_cutoff_min),
 last_year_cutoff_max: formData.last_year_cutoff_max === '' ? null : Number(formData.last_year_cutoff_max)
 };
 onSave(program.id, payload);
 };

 // Sync form when program prop changes (in case modal is reused)
 useEffect(() => {
 const initial = {
 program_name: (program && (program.name || program.program_name)) || '',
 program_fee: (program && (program.fee || program.program_fee)) || '',
 application_deadline: program && (program.deadline || program.application_deadline)
 ? (program.deadline || program.application_deadline).split?.('T')?.[0] || ''
 : '',
 duration: (program && (program.duration || program.program_duration)) || '',
 degree_type_id: (program && (program.degree_type_id || program.degree_type)) || '',
 field_of_study_id: (program && (program.field_of_study_id || program.field_of_study)) || '',
 description: (program && (program.description || program.program_description)) || '',
 last_year_cutoff_min: (program && (program.last_year_cutoff_min ?? program.min_cutoff ?? '')) ?? '',
 last_year_cutoff_max: (program && (program.last_year_cutoff_max ?? program.max_cutoff ?? '')) ?? ''
 };
 setFormData(initial);
 }, [program]);

 return (
 <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4">
 <div className="relative bg-white rounded-2xl border border-slate-200 shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
 <div className="p-6">
 <div className="flex justify-between items-center mb-6">
 <div>
 <h3 className="text-xl font-bold text-slate-800">Edit Program</h3>
 <p className="text-sm text-slate-500 mt-0.5">Update program details and merit cutoffs</p>
 </div>
 <button
 onClick={onClose}
 className="text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 transition-all"
 >
 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>

 <div className="space-y-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Program Name *</label>
 <input
 type="text"
 value={formData.program_name}
 onChange={(e) => handleInputChange('program_name', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 />
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Program Fee (PKR)</label>
 <input
 type="number"
 value={formData.program_fee}
 onChange={(e) => handleInputChange('program_fee', e.target.value)}
 placeholder="e.g., 500000"
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Application Deadline</label>
 <input
 type="date"
 value={formData.application_deadline}
 onChange={(e) => handleInputChange('application_deadline', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 />
 </div>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Duration</label>
 <select
 value={formData.duration}
 onChange={(e) => handleInputChange('duration', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 >
 <option value="">Select Duration</option>
 {getDurationOptions(formData.duration).map(d => (
   <option key={d} value={d}>{d}</option>
 ))}
 </select>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Degree Type</label>
 <select
 value={formData.degree_type_id}
 onChange={(e) => handleInputChange('degree_type_id', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 >
 <option value="">Select Degree Type</option>
 {degreeTypes.map(type => (
 <option key={type.id} value={type.id}>{type.name}</option>
 ))}
 </select>
 </div>
 </div>

 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Field of Study</label>
 <select
 value={formData.field_of_study_id}
 onChange={(e) => handleInputChange('field_of_study_id', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 >
 <option value="">Select Field of Study</option>
 {fieldsOfStudy.map(field => (
 <option key={field.id} value={field.id}>{field.name}</option>
 ))}
 </select>
 </div>

 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Description</label>
 <textarea
 value={formData.description}
 onChange={(e) => handleInputChange('description', e.target.value)}
 placeholder="Brief description of the program..."
 rows="4"
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all resize-none"
 />
 </div>

 {/* Merit Cutoff Fields */}
 <div className="bg-emerald-50/60 border border-emerald-200/60 rounded-xl p-4">
 <h4 className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2">
 <span>🎯</span>
 Merit Cutoff (For Recommendations)
 </h4>
 <p className="text-xs text-emerald-700 mb-3">
 Set the merit cutoff range to help students find your program through our recommendation system
 </p>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
 Minimum Merit % *
 </label>
 <input
 type="number"
 step="0.01"
 min="0"
 max="100"
 value={formData.last_year_cutoff_min}
 onChange={(e) => handleInputChange('last_year_cutoff_min', e.target.value)}
 placeholder="e.g., 70"
 className="w-full px-3.5 py-2.5 bg-white border border-emerald-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
 />
 <p className="text-xs text-slate-500 mt-1">Minimum percentage required</p>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
 Maximum Merit % (Optional)
 </label>
 <input
 type="number"
 step="0.01"
 min="0"
 max="100"
 value={formData.last_year_cutoff_max}
 onChange={(e) => handleInputChange('last_year_cutoff_max', e.target.value)}
 placeholder="e.g., 85"
 className="w-full px-3.5 py-2.5 bg-white border border-emerald-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
 />
 <p className="text-xs text-slate-500 mt-1">Maximum percentage (if applicable)</p>
 </div>
 </div>
 </div>
 </div>

 <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
 <button
 type="button"
 onClick={onClose}
 className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-sm transition-all active:scale-95"
 >
 Cancel
 </button>
 <button
 type="button"
 onClick={handleSubmit}
 disabled={saving || !formData.program_name.trim()}
 className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold rounded-xl text-sm shadow-md shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
 >
 {saving ? 'Saving...' : 'Save Changes'}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
});

// Enhanced Program Form Component
const AddProgramForm = React.memo(({ newProgram, onProgramChange, handleAddProgram, addingProgram, degreeTypes, fieldsOfStudy }) => {
 const [showAdvanced, setShowAdvanced] = useState(false);
 const [programData, setProgramData] = useState({
 program_name: '',
 program_fee: '',
 application_deadline: '',
 duration: '',
 degree_type_id: '',
 field_of_study_id: '',
 description: '',
 last_year_cutoff_min: '',
 last_year_cutoff_max: ''
 });

 const handleInputChange = (field, value) => {
 setProgramData(prev => ({ ...prev, [field]: value }));
 if (field === 'program_name') {
 onProgramChange({ target: { value } });
 }
 };

 const handleSubmit = () => {
 handleAddProgram(programData);
 setProgramData({
 program_name: '',
 program_fee: '',
 application_deadline: '',
 duration: '',
 degree_type_id: '',
 field_of_study_id: '',
 description: '',
 last_year_cutoff_min: '',
 last_year_cutoff_max: ''
 });
 setShowAdvanced(false);
 };

 return (
 <div className="mb-6 p-5 bg-slate-50 border border-slate-200 rounded-2xl">
 <div className="space-y-4">
 {/* Basic Program Info */}
 <div className="flex gap-3">
 <input
 key="program-input"
 type="text"
 value={programData.program_name}
 onChange={(e) => handleInputChange('program_name', e.target.value)}
 placeholder="Enter program name (e.g., Computer Science, Engineering)"
 className="flex-1 px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 autoComplete="off"
 />
 <button
 type="button"
 onClick={() => setShowAdvanced(!showAdvanced)}
 className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl text-sm transition-all active:scale-95"
 >
 {showAdvanced ? 'Basic' : 'Advanced'}
 </button>
 <button
 onClick={handleSubmit}
 disabled={addingProgram || !programData.program_name.trim()}
 className="px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold rounded-xl text-sm shadow-md shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all active:scale-95"
 >
 <PlusIcon className="w-4 h-4" />
 <span>{addingProgram ? 'Adding...' : 'Add Program'}</span>
 </button>
 </div>

 {/* Advanced Fields */}
 {showAdvanced && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Program Fee (PKR)</label>
 <input
 type="number"
 value={programData.program_fee}
 onChange={(e) => handleInputChange('program_fee', e.target.value)}
 placeholder="e.g., 500000"
 className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Application Deadline</label>
 <input
 type="date"
 value={programData.application_deadline}
 onChange={(e) => handleInputChange('application_deadline', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Duration</label>
 <select
 value={programData.duration}
 onChange={(e) => handleInputChange('duration', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 >
 <option value="">Select Duration</option>
 {getDurationOptions(programData.duration).map(d => (
   <option key={d} value={d}>{d}</option>
 ))}
 </select>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Degree Type</label>
 <select
 value={programData.degree_type_id}
 onChange={(e) => handleInputChange('degree_type_id', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 >
 <option value="">Select Degree Type</option>
 {degreeTypes.map(type => (
 <option key={type.id} value={type.id}>{type.name}</option>
 ))}
 </select>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Field of Study</label>
 <select
 value={programData.field_of_study_id}
 onChange={(e) => handleInputChange('field_of_study_id', e.target.value)}
 className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 >
 <option value="">Select Field of Study</option>
 {fieldsOfStudy.map(field => (
 <option key={field.id} value={field.id}>{field.name}</option>
 ))}
 </select>
 </div>
 <div className="md:col-span-2">
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Description</label>
 <textarea
 value={programData.description}
 onChange={(e) => handleInputChange('description', e.target.value)}
 placeholder="Brief description of the program..."
 rows="3"
 className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all resize-none"
 />
 </div>

 {/* Merit Cutoff Section */}
 <div className="md:col-span-2 p-4 bg-emerald-50/60 border border-emerald-200/60 rounded-xl">
 <h4 className="text-sm font-bold text-emerald-800 mb-2 flex items-center gap-2">
 <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
 <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
 </svg>
 Merit Cutoff Marks (For Recommendations)
 </h4>
 <p className="text-xs text-emerald-700 mb-3">
 Add last year's merit cutoff to help students find suitable programs through our recommendation system
 </p>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
 Minimum Merit % <span className="text-rose-500">*</span>
 </label>
 <input
 type="number"
 step="0.01"
 min="0"
 max="100"
 value={programData.last_year_cutoff_min}
 onChange={(e) => handleInputChange('last_year_cutoff_min', e.target.value)}
 placeholder="e.g., 70"
 className="w-full px-3.5 py-2.5 bg-white border border-emerald-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
 />
 <p className="text-xs text-slate-500 mt-1">Minimum percentage required</p>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
 Maximum Merit % (Optional)
 </label>
 <input
 type="number"
 step="0.01"
 min="0"
 max="100"
 value={programData.last_year_cutoff_max}
 onChange={(e) => handleInputChange('last_year_cutoff_max', e.target.value)}
 placeholder="e.g., 85"
 className="w-full px-3.5 py-2.5 bg-white border border-emerald-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
 />
 <p className="text-xs text-slate-500 mt-1">Maximum percentage (if applicable)</p>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
});

// Move UniversityProfile outside to prevent recreation
const UniversityProfile = React.memo(({
 profileData,
 user,
 uploading,
 profilePictureRef,
 coverPhotoRef,
 handleImageUpload,
 newProgram,
 onProgramChange,
 handleAddProgram,
 addingProgram,
 handleDeleteProgram,
 aboutMe,
 setAboutMe,
 websiteLink,
 setWebsiteLink,
 handleSaveProfile,
 savingProfile,
 isEditingAbout,
 setIsEditingAbout,
 degreeTypes,
 fieldsOfStudy
}) => (
 <div className="space-y-6">
 {/* Cover Photo Section */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
 <div className="relative">
 {profileData?.cover_photo ? (
 <img
 src={profileData.cover_photo}
 alt="Cover"
 className="w-full h-48 object-cover rounded-t-2xl"
 />
 ) : (
 <div className="w-full h-48 bg-gradient-to-br from-primary-600 via-indigo-600 to-primary-800 flex items-center justify-center rounded-t-2xl">
 <BuildingOfficeIcon className="w-16 h-16 text-white/30" />
 </div>
 )}
 <button
 onClick={() => coverPhotoRef.current?.click()}
 disabled={uploading}
 className="absolute top-4 right-4 bg-white hover:bg-white text-slate-700 p-2 rounded-xl shadow-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
 >
 <PencilIcon className="w-4 h-4" />
 </button>
 <input
 ref={coverPhotoRef}
 type="file"
 accept="image/*"
 onChange={(e) => handleImageUpload(e.target.files[0], 'cover')}
 className="hidden"
 />
 </div>

 {/* Profile Picture and University Info */}
 <div className="px-6 pb-6">
 <div className="flex items-center gap-5">
 <div className="relative flex-shrink-0 -mt-12">
 {profileData?.profile_picture ? (
 <img
 src={profileData.profile_picture}
 alt="Profile"
 className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg"
 />
 ) : (
 <div className="w-24 h-24 rounded-2xl bg-slate-100 border-4 border-white shadow-lg flex items-center justify-center">
 <BuildingOfficeIcon className="w-12 h-12 text-slate-400" />
 </div>
 )}
 <button
 onClick={() => profilePictureRef.current?.click()}
 disabled={uploading}
 className="absolute -bottom-2 -right-2 bg-primary-600 hover:bg-primary-700 text-white p-1.5 rounded-xl shadow-lg transition-all hover:scale-110 active:scale-95 disabled:opacity-50"
 >
 <PencilIcon className="w-3 h-3" />
 </button>
 <input
 ref={profilePictureRef}
 type="file"
 accept="image/*"
 onChange={(e) => handleImageUpload(e.target.files[0], 'profile')}
 className="hidden"
 />
 </div>
 <div className="flex-1 pt-2">
 <h3 className="text-xl font-bold text-slate-800">{profileData?.university_name || user?.name}</h3>
 <p className="text-sm font-semibold text-primary-600">University</p>
 <p className="text-xs text-slate-400 mt-0.5">
 Upload cover photo (820×312px) and profile picture (180×180px)
 </p>
 </div>
 </div>
 </div>
 </div>

 {/* University Information */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <h3 className="text-base font-bold text-slate-800 mb-5">University Information</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <BuildingOfficeIcon className="h-4 w-4 text-primary-600" />
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">University Name</p>
 <p className="text-sm font-semibold text-slate-800 mt-0.5">{profileData?.university_name || user?.name || 'Not provided'}</p>
 </div>
 </div>
 <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <UserCircleIcon className="h-4 w-4 text-primary-600" />
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Contact Person</p>
 <p className="text-sm font-semibold text-slate-800 mt-0.5">{profileData?.name || 'Not provided'}</p>
 </div>
 </div>
 <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <EnvelopeIcon className="h-4 w-4 text-primary-600" />
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email</p>
 <p className="text-sm font-semibold text-slate-800 mt-0.5">{profileData?.email || user?.email || 'Not provided'}</p>
 </div>
 </div>
 <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <PhoneIcon className="h-4 w-4 text-primary-600" />
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Phone</p>
 <p className="text-sm font-semibold text-slate-800 mt-0.5">{profileData?.phone || 'Not provided'}</p>
 </div>
 </div>
 <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <CalendarIcon className="h-4 w-4 text-primary-600" />
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Established</p>
 <p className="text-sm font-semibold text-slate-800 mt-0.5">{profileData?.established_year || 'Not provided'}</p>
 </div>
 </div>
 <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <MapPinIcon className="h-4 w-4 text-primary-600" />
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Address</p>
 <p className="text-sm font-semibold text-slate-800 mt-0.5">{profileData?.address || 'Not provided'}</p>
 </div>
 </div>
 </div>
 </div>

 {/* About Me Section */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex justify-between items-center mb-5">
 <div>
 <h3 className="text-base font-bold text-slate-800">About University</h3>
 </div>
 {!isEditingAbout && (
 <button
 onClick={() => setIsEditingAbout(true)}
 className="text-slate-500 hover:text-primary-600 p-2 rounded-xl hover:bg-primary-50 transition-all"
 title="Edit About Section"
 >
 <PencilIcon className="w-4 h-4" />
 </button>
 )}
 </div>

 {isEditingAbout ? (
 <div className="space-y-4">
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
 About Me / University Description
 </label>
 <textarea
 rows={4}
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all resize-none"
 placeholder="Tell students about your university, facilities, achievements, and what makes you unique..."
 value={aboutMe}
 onChange={(e) => setAboutMe(e.target.value)}
 />
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
 Website Link
 </label>
 <input
 type="url"
 className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 transition-all"
 placeholder="https://your-university-website.edu"
 value={websiteLink}
 onChange={(e) => setWebsiteLink(e.target.value)}
 />
 </div>
 <div className="flex justify-end gap-3">
 <button
 type="button"
 onClick={() => {
 setIsEditingAbout(false);
 // Reset to original values
 setAboutMe(profileData?.about_me || profileData?.about_university || profileData?.campus_info || '');
 setWebsiteLink(profileData?.website_link || profileData?.website_url || '');
 }}
 className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-sm transition-all active:scale-95"
 >
 Cancel
 </button>
 <button
 type="button"
 onClick={async () => {
 await handleSaveProfile();
 setIsEditingAbout(false);
 }}
 disabled={savingProfile}
 className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:bg-slate-300 text-white font-semibold rounded-xl text-sm shadow-md shadow-primary-500/20 disabled:shadow-none transition-all active:scale-95"
 >
 {savingProfile ? 'Saving...' : 'Save Changes'}
 </button>
 </div>
 </div>
 ) : (
 <div className="space-y-4">
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
 About Us
 </label>
 <div className="text-slate-700 text-sm whitespace-pre-wrap bg-slate-50 border border-slate-100 p-4 rounded-xl min-h-[100px]">
 {aboutMe || <span className="text-slate-400 italic">No description provided yet. Click the pencil icon to add one.</span>}
 </div>
 </div>
 <div>
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
 Website Link
 </label>
 <div className="text-sm bg-slate-50 border border-slate-100 p-4 rounded-xl">
 {websiteLink ? (
 <a
 href={websiteLink}
 target="_blank"
 rel="noopener noreferrer"
 className="text-primary-600 hover:text-primary-700 hover:underline font-medium"
 >
 {websiteLink}
 </a>
 ) : (
 <span className="text-slate-400 italic">No website link provided yet. Click the pencil icon to add one.</span>
 )}
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Campus Facilities - Simple Version */}
 {profileData && profileData.role === 'university' && (
 <SimpleCampusFacilities profileData={profileData} />
 )}


 {/* Applicant Management */}
 {/* <div className="bg-white rounded-lg shadow-md p-6">
 <h3 className="text-lg font-semibold text-slate-900 mb-4">Student Applications</h3>
 <div className="text-center py-8 text-slate-500">
 <UserCircleIcon className="h-12 w-12 mx-auto mb-4 text-slate-300" />
 <p>No applications received yet</p>
 <p className="text-sm">Students will appear here when they apply to your programs</p>
 </div>
 </div> */}
 </div>
));

// Student Applications Tab Component
const StudentApplications = React.memo(({ navigate }) => {
 const [applications, setApplications] = useState([]);
 const [examAttempts, setExamAttempts] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const fetchApplications = async () => {
 try {
 const token = localStorage.getItem('token');
 const [appResponse, attemptsResponse] = await Promise.all([
 fetch(`${API_BASE_URL}/application/my_applications`, {
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 }),
 fetch(API_ENDPOINTS.STUDENT_EXAM_ATTEMPTS, {
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 })
 ]);

 if (appResponse.ok) {
 const appData = await appResponse.json();
 setApplications(appData.applications || []);
 }

 if (attemptsResponse.ok) {
 const attemptsData = await attemptsResponse.json();
 setExamAttempts(attemptsData || []);
 }
 } catch (error) {
 console.error('Error fetching student profile data:', error);
 } finally {
 setLoading(false);
 }
 };

 fetchApplications();
 }, []);

 if (loading) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6 py-12 flex justify-center">
      <DakhlaButterflyLoader text="Loading applications..." />
    </div>
  );
 }

 return (
 <div className="space-y-6">
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
 <div>
 <h3 className="text-lg font-bold text-slate-800">My Applications</h3>
 <p className="text-sm text-slate-500 mt-1">
 {applications.length > 0
 ? `You have ${applications.length} application${applications.length > 1 ? 's' : ''} in progress`
 : 'Track your university applications here'}
 </p>
 </div>
 <button
 onClick={() => navigate('/application-form')}
 className="w-full sm:w-auto bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-semibold py-2 px-5 rounded-xl shadow-md shadow-primary-500/10 hover:shadow-lg hover:shadow-primary-500/20 active:scale-[0.98] transition-all duration-300 ease-out flex items-center justify-center space-x-2"
 >
 <PlusIcon className="w-4 h-4" />
 <span>New Application</span>
 </button>
 </div>

 {applications.length > 0 ? (
 <div className="space-y-4">
 {applications.map((app) => (
 <div key={app.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-primary-100 hover:shadow-sm hover:scale-[1.005] transition-all duration-300 ease-out">
 <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
 <div className="flex-1">
 <div className="flex items-center space-x-3 mb-3">
 <div className="p-2 bg-primary-50 rounded-xl">
 <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
 </div>
 <div>
 <h4 className="font-bold text-slate-800 text-lg leading-tight">
 {app.university_name || 'University'}
 </h4>
 <p className="text-sm font-semibold text-slate-500 mt-0.5">
 {app.program_name || 'Program'}
 </p>
 </div>
 </div>

 <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</p>
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold mt-1 ${app.status === 'Pending' ? 'bg-amber-50 border border-amber-200/50 text-amber-700' :
 app.status === 'Under Review' || app.status === 'Under_Review' ? 'bg-blue-50 border border-blue-200/50 text-blue-700' :
 app.status === 'Accepted' ? 'bg-emerald-50 border border-emerald-200/50 text-emerald-700' :
 app.status === 'Rejected' ? 'bg-rose-50 border border-rose-200/50 text-rose-700' :
 'bg-slate-50 border border-slate-200 text-slate-600'
 }`}>
 {app.status?.replace('_', ' ')}
 </span>
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Submitted</p>
 <p className="text-sm font-bold text-slate-700 mt-1">
 {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'N/A'}
 </p>
 </div>
 <div className="col-span-2 md:col-span-1">
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Application ID</p>
 <p className="text-sm font-bold text-slate-700 mt-1">#{app.id}</p>
 </div>
 </div>

 {/* Rejection Reason */}
 {app.status === 'Rejected' && app.rejection_reason && (
 <div className="mt-4 p-4 bg-rose-50/50 border border-rose-100 rounded-xl">
 <p className="text-xs font-bold text-rose-800 mb-1">Rejection Reason:</p>
 <p className="text-sm text-rose-700 font-medium">{app.rejection_reason}</p>
 </div>
 )}
 </div>

 <button
 onClick={() => navigate(`/university/${app.university_id}`)}
 className="self-end sm:self-start text-primary-600 hover:text-primary-700 p-2.5 rounded-xl hover:bg-primary-50 transition-all active:scale-95 border border-slate-100"
 title="View University"
 >
 <EyeIcon className="w-5 h-5" />
 </button>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-12 text-slate-400">
 <AcademicCapIcon className="h-16 w-16 mx-auto mb-4 text-slate-300" />
 <p className="text-lg font-bold text-slate-700">No Applications Yet</p>
 <p className="text-sm mt-1 max-w-sm mx-auto">You haven't applied to any universities yet. Start by browsing universities and matching program cutoffs.</p>

 <div className="flex justify-center mt-6">
 <button
 onClick={() => navigate('/partners')}
 className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 px-6 rounded-xl border border-slate-200 hover:border-slate-300 active:scale-[0.98] transition-all duration-300 ease-out"
 >
 Browse Universities
 </button>
 </div>
 </div>
 )}
 </div>

 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
 <div>
 <h3 className="text-lg font-bold text-slate-800">My Exam Attempts</h3>
 <p className="text-sm text-slate-500 mt-1">Marks and status for university entrance exams you attempted.</p>
 </div>
 </div>

 {examAttempts.length === 0 ? (
 <div className="text-center py-8 text-slate-400">
 <p className="text-sm">No exam attempts recorded yet.</p>
 </div>
 ) : (
 <div className="overflow-x-auto rounded-xl border border-slate-200">
 <table className="min-w-full text-sm">
 <thead>
 <tr className="text-left text-slate-500 bg-slate-50 border-b border-slate-200 font-semibold">
 <th className="py-3.5 px-4">Exam</th>
 <th className="py-3.5 px-4">University</th>
 <th className="py-3.5 px-4 text-center">Obtained</th>
 <th className="py-3.5 px-4 text-center">Total</th>
 <th className="py-3.5 px-4">Status</th>
 <th className="py-3.5 px-4">Submitted</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100 bg-white">
 {examAttempts.map((attempt) => (
 <tr key={attempt.attempt_id} className="hover:bg-slate-50 transition-colors">
 <td className="py-3.5 px-4 font-semibold text-slate-800">{attempt.title}</td>
 <td className="py-3.5 px-4 text-slate-600">{attempt.university}</td>
 <td className="py-3.5 px-4 text-center font-bold text-slate-800">{attempt.obtained_marks}</td>
 <td className="py-3.5 px-4 text-center text-slate-600">{attempt.total_marks}</td>
 <td className="py-3.5 px-4">
 <span className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-lg border ${attempt.status === 'Passed'
 ? 'bg-emerald-50 border-emerald-200/50 text-emerald-700'
 : 'bg-rose-50 border-rose-200/50 text-rose-700'
 }`}>
 {attempt.status}
 </span>
 </td>
 <td className="py-3.5 px-4 text-slate-500">{attempt.submitted_at ? new Date(attempt.submitted_at).toLocaleDateString() : '-'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 </div>
 );
});

// University Programs Tab Component
const UniversityPrograms = React.memo(React.forwardRef(({
 profileData,
 newProgram,
 onProgramChange,
 handleAddProgram,
 addingProgram,
 handleDeleteProgram,
 handleEditProgram,
 editingProgram,
 setEditingProgram,
 savingProgram,
 degreeTypes,
 fieldsOfStudy
}, ref) => (
 <div ref={ref} className="space-y-6">
 <div className="card relative overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex justify-between items-center mb-6">
 <div>
 <h3 className="text-base font-bold text-slate-800">Programs Offered</h3>
 <p className="text-xs text-slate-500 mt-0.5">Manage your university's academic programs</p>
 </div>
 </div>

 {/* Add Program Form */}
 <AddProgramForm
 newProgram={newProgram}
 onProgramChange={onProgramChange}
 handleAddProgram={handleAddProgram}
 addingProgram={addingProgram}
 degreeTypes={degreeTypes}
 fieldsOfStudy={fieldsOfStudy}
 />

 {/* Programs List */}
 {profileData?.programs && profileData.programs.length > 0 ? (
 <div className="space-y-3">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Current Programs ({profileData.programs.length})</h4>
 {profileData.programs.map((program) => (
 <div key={program.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md hover:border-primary-100 transition-all duration-200">
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-3">
 <div className="p-2 bg-primary-50 rounded-xl flex-shrink-0">
 <AcademicCapIcon className="h-5 w-5 text-primary-600" />
 </div>
 <div>
 <h4 className="font-bold text-slate-800">
 {formatProgramDisplay(program.name || program.program_name, program.degree_type)}
 </h4>
 </div>
 </div>

 {program.description && (
 <p className="text-sm text-slate-600 mb-4 ml-1">{program.description}</p>
 )}

 <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
 {program.fee && (
 <div className="flex items-center gap-2 p-2.5 bg-emerald-50/50 border border-emerald-100 rounded-xl">
 <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tuition Fee</span>
 <span className="text-xs font-bold text-emerald-700">
 {formatFeeDisplay(program.fee, program.fee_type || 'semester')}
 </span>
 </div>
 )}
 {program.duration && (
 <div className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-100 rounded-xl">
 <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Duration</span>
 <span className="text-xs font-bold text-slate-700">{program.duration}</span>
 </div>
 )}
 {program.deadline && (
 <div className="flex items-center gap-2 p-2.5 bg-rose-50/50 border border-rose-100 rounded-xl">
 <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Deadline</span>
 <span className="text-xs font-bold text-rose-600">
 {new Date(program.deadline).toLocaleDateString()}
 </span>
 </div>
 )}
 </div>

 {formatMeritCutoff(program) && (
 <div className="mb-3 flex items-center gap-2">
 <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Merit Cutoff</span>
 <span className="px-2.5 py-0.5 bg-emerald-50 border border-emerald-200/60 text-emerald-700 text-xs font-bold rounded-lg">{formatMeritCutoff(program)}</span>
 </div>
 )}

 <div className="flex items-center gap-2">
 <span className="px-2.5 py-1 bg-emerald-50 border border-emerald-200/60 text-emerald-700 text-xs font-bold rounded-lg">
 Active
 </span>
 <span className="text-xs text-slate-400">
 Available for applications
 </span>
 </div>
 </div>

 <div className="flex items-center gap-2 ml-4">
 <button
 onClick={() => {
 const normalized = {
 ...program,
 last_year_cutoff_min: program.last_year_cutoff_min ?? program.min_cutoff ?? program.minimum_merit ?? program.min ?? null,
 last_year_cutoff_max: program.last_year_cutoff_max ?? program.max_cutoff ?? program.maximum_merit ?? program.max ?? null,
 id: program.id ?? program.program_id ?? program._id
 };
 setEditingProgram(normalized);
 }}
 className="text-primary-600 hover:text-primary-700 p-2 rounded-xl hover:bg-primary-50 transition-all active:scale-95"
 title="Edit program"
 >
 <PencilIcon className="w-4 h-4" />
 </button>
 <button
 onClick={() => handleDeleteProgram(program.id)}
 className="text-rose-400 hover:text-rose-600 p-2 rounded-xl hover:bg-rose-50 transition-all active:scale-95"
 title="Delete program"
 >
 <TrashIcon className="w-4 h-4" />
 </button>
 </div>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-16 text-slate-400">
 <AcademicCapIcon className="h-16 w-16 mx-auto mb-4 text-slate-200" />
 <p className="text-base font-bold text-slate-500">No Programs Added</p>
 <p className="text-sm mt-1 text-slate-400">Start by adding your first academic program above.</p>
 <p className="text-sm text-slate-400">Students will be able to apply to these programs.</p>
 </div>
 )}
 </div>
 </div>
)));

// University Applicants Tab Component
const UniversityApplicants = React.memo(React.forwardRef((props, ref) => {
 const { token } = useAuth();
 const { showSuccess, showError } = useToast();
 const [applications, setApplications] = useState([]);
 const [loading, setLoading] = useState(true);
 const [programs, setPrograms] = useState([]);
 const [filters, setFilters] = useState({
 program_id: '',
 status: ''
 });
 const [selectedCandidate, setSelectedCandidate] = useState(null);
 const [showCandidateModal, setShowCandidateModal] = useState(false);
 const [updatingStatus, setUpdatingStatus] = useState(null);
 const [degreeTypes, setDegreeTypes] = useState([]);
 const [fieldsOfStudy, setFieldsOfStudy] = useState([]);
 const [docViewer, setDocViewer] = useState({ url: '', title: '' });
 const [showRejectionModal, setShowRejectionModal] = useState(false);
 const [rejectionReason, setRejectionReason] = useState('');
 const [applicationToReject, setApplicationToReject] = useState(null);

 useEffect(() => {
 fetchApplications();
 fetchPrograms();
 }, [filters]);

 useEffect(() => {
 // Fetch degree types and fields of study once on mount
 fetchDegreeTypes();
 fetchFieldsOfStudy();
 }, []);

 const fetchPrograms = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/university/programs`, {
 headers: getAuthHeaders(token)
 });
 if (response.ok) {
 const data = await response.json();
 setPrograms(data);
 }
 } catch (error) {
 console.error('Error fetching programs:', error);
 }
 };

 const fetchDegreeTypes = async () => {
 try {
 const response = await fetch(API_ENDPOINTS.DEGREE_TYPES);
 if (response.ok) {
 const data = await response.json();
 setDegreeTypes(data);
 }
 } catch (error) {
 console.error('Error fetching degree types:', error);
 }
 };

 const fetchFieldsOfStudy = async () => {
 try {
 const response = await fetch(API_ENDPOINTS.FIELDS_OF_STUDY);
 if (response.ok) {
 const data = await response.json();
 setFieldsOfStudy(data);
 }
 } catch (error) {
 console.error('Error fetching fields of study:', error);
 }
 };

 const fetchApplications = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams();
 if (filters.program_id) params.append('program_id', filters.program_id);
 if (filters.status) params.append('status', filters.status);

 const response = await fetch(
 `${API_BASE_URL}/university/applications?${params.toString()}`,
 {
 headers: getAuthHeaders(token)
 }
 );

 if (response.ok) {
 const data = await response.json();
 setApplications(data.applications || []);
 } else {
 showError('Failed to load applications');
 }
 } catch (error) {
 console.error('Error fetching applications:', error);
 showError('Error loading applications');
 } finally {
 setLoading(false);
 }
 };

 const handleStatusUpdate = async (applicationId, newStatus, rejectionReason = null) => {
 try {
 setUpdatingStatus(applicationId);

 const requestBody = { status: newStatus };
 if (newStatus === 'Rejected' && rejectionReason) {
 requestBody.rejection_reason = rejectionReason;
 }

 const response = await fetch(
 `${API_BASE_URL}/university/applications/${applicationId}/status`,
 {
 method: 'PATCH',
 headers: getAuthHeaders(token),
 body: JSON.stringify(requestBody)
 }
 );

 if (response.ok) {
 await response.json();
 showSuccess(`Application ${newStatus.replace('_', ' ').toLowerCase()} successfully`);
 fetchApplications(); // Refresh the list
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to update status');
 }
 } catch (error) {
 console.error('Error updating status:', error);
 showError(`Error updating application status: ${error.message}`);
 } finally {
 setUpdatingStatus(null);
 }
 };

 const handleRejectClick = (applicationId) => {
 setApplicationToReject(applicationId);
 setRejectionReason('');
 setShowRejectionModal(true);
 };

 const handleRejectConfirm = async () => {
 if (!rejectionReason.trim()) {
 showError('Please provide a reason for rejection');
 return;
 }

 await handleStatusUpdate(applicationToReject, 'Rejected', rejectionReason);
 setShowRejectionModal(false);
 setApplicationToReject(null);
 setRejectionReason('');
 };

 const handleRejectCancel = () => {
 setShowRejectionModal(false);
 setApplicationToReject(null);
 setRejectionReason('');
 };

 const handleViewCandidate = async (candidateId) => {
 try {
 const response = await fetch(
 `${API_BASE_URL}/university/candidate/${candidateId}`,
 {
 headers: getAuthHeaders(token)
 }
 );

 if (response.ok) {
 const data = await response.json();
 setSelectedCandidate(data);
 setShowCandidateModal(true);
 } else {
 showError('Failed to load candidate profile');
 }
 } catch (error) {
 console.error('Error fetching candidate:', error);
 showError('Error loading candidate profile');
 }
 };

 const getStatusBadgeClass = (status) => {
 switch (status) {
 case 'Pending':
 return 'bg-yellow-100 text-yellow-800';
 case 'Under_Review':
 return 'bg-blue-100 text-blue-800';
 case 'Accepted':
 return 'bg-green-100 text-green-800';
 case 'Rejected':
 return 'bg-red-100 text-red-800';
 default:
 return 'bg-slate-100 text-slate-800';
 }
 };

 const getDegreeTypeName = (id) => {
 if (!id) return 'Unknown';
 if (degreeTypes.length === 0) return 'Loading...';
 const degreeType = degreeTypes.find(type => type.id === id);
 return degreeType ? degreeType.name : `Degree Type ID ${id}`;
 };

 const getFieldOfStudyName = (id) => {
 if (!id) return 'Unknown';
 if (fieldsOfStudy.length === 0) return 'Loading...';
 const field = fieldsOfStudy.find(f => f.id === id);
 return field ? field.name : `Field ID ${id}`;
 };

 return (
 <div ref={ref} className="space-y-6">
 <div className="bg-white rounded-lg shadow-md p-6">
 <div className="flex justify-between items-center mb-6">
 <div>
 <h3 className="text-lg font-semibold text-slate-900">Student Applications</h3>
 <p className="text-sm text-slate-600 mt-1">
 {applications.length} application{applications.length !== 1 ? 's' : ''} received
 </p>
 </div>
 <div className="flex items-center space-x-4">
 <select
 value={filters.program_id}
 onChange={(e) => setFilters({ ...filters, program_id: e.target.value })}
 className="px-3 py-2 border border-slate-300 rounded-md text-sm"
 >
 <option value="">All Programs</option>
 {programs.map((program) => (
 <option key={program.id} value={program.id}>
 {program.program_name}
 </option>
 ))}
 </select>
 <select
 value={filters.status}
 onChange={(e) => setFilters({ ...filters, status: e.target.value })}
 className="px-3 py-2 border border-slate-300 rounded-md text-sm"
 >
 <option value="">All Status</option>
 <option value="Pending">Pending</option>
 <option value="Under_Review">Under Review</option>
 <option value="Accepted">Accepted</option>
 <option value="Rejected">Rejected</option>
 </select>
 </div>
 </div>

 {loading ? (
 <div className="text-center py-12">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
 <p className="text-slate-500 mt-4">Loading applications...</p>
 </div>
 ) : applications.length === 0 ? (
 <div className="text-center py-12 text-slate-500">
 <UserCircleIcon className="h-16 w-16 mx-auto mb-4 text-slate-300" />
 <p className="text-lg font-medium">No Applications Found</p>
 <p className="text-sm mt-2">
 {filters.program_id || filters.status
 ? 'Try adjusting your filters'
 : 'When students apply to your programs, they will appear here'}
 </p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-slate-200">
 <thead className="bg-slate-50">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
 Student
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
 Program
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
 Status
 </th>
 <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
 Submitted
 </th>
 <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
 Actions
 </th>
 </tr>
 </thead>
 <tbody className="bg-white divide-y divide-slate-200">
 {applications.map((application) => (
 <tr key={application.id} className="hover:bg-slate-50">
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center">
 <div className="flex-shrink-0 h-10 w-10">
 <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
 <span className="text-primary-600 font-medium text-sm">
 {application.student_name?.charAt(0) || 'S'}
 </span>
 </div>
 </div>
 <div className="ml-4">
 <div className="text-sm font-medium text-slate-900">
 {application.student_name}
 </div>
 <button
 onClick={() => handleViewCandidate(application.candidate_id)}
 className="text-xs text-primary-600 hover:text-primary-800"
 >
 View Profile
 </button>
 </div>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="text-sm text-slate-900">{application.program_name}</div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(application.status)}`}>
 {application.status.replace('_', ' ')}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
 {new Date(application.submitted_at).toLocaleDateString()}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
 <div className="flex justify-end space-x-2">
 {(application.status === 'Pending' || application.status === 'Under_Review') && (
 <>
 <button
 onClick={() => handleViewCandidate(application.candidate_id)}
 className="text-blue-600 hover:text-blue-800 font-medium"
 >
 Review
 </button>
 <button
 onClick={() => handleRejectClick(application.id)}
 disabled={updatingStatus === application.id}
 className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
 >
 Reject
 </button>
 {/* Accept button - functionality to be decided */}
 </>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>

 {/* Candidate Profile Modal */}
 {showCandidateModal && selectedCandidate && (
 <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
 <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white mb-10">
 <div className="flex justify-between items-center mb-4 pb-4 border-b">
 <h3 className="text-xl font-semibold text-slate-900">Candidate Profile</h3>
 <button
 onClick={() => setShowCandidateModal(false)}
 className="text-slate-400 hover:text-slate-600 transition-colors"
 >
 <XMarkIcon className="h-6 w-6" />
 </button>
 </div>

 <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-2">
 {/* Basic Info */}
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <UserCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
 Basic Information
 </h4>

 {/* Profile picture + name header */}
 <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-200">
 {selectedCandidate.demographics?.profile_picture_url ? (
 <img
 src={selectedCandidate.demographics.profile_picture_url}
 alt="Profile"
 className="w-20 h-20 rounded-full object-cover border-2 border-primary-200 shrink-0 shadow-sm"
 />
 ) : (
 <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center shrink-0 border-2 border-slate-300">
 <UserCircleIcon className="h-10 w-10 text-slate-400" />
 </div>
 )}
 <div>
 <p className="text-base font-semibold text-slate-900">{selectedCandidate.student_name}</p>
 <p className="text-sm text-slate-500 mt-0.5">
 CNIC: <span className="font-medium text-slate-700">{selectedCandidate.cnic}</span>
 </p>
 {!selectedCandidate.demographics?.profile_picture_url && (
 <p className="text-xs text-amber-600 mt-1">No profile picture uploaded</p>
 )}
 </div>
 </div>

 </div>

 {/* Contact Details */}
 {selectedCandidate.contact_details && (
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <PhoneIcon className="h-5 w-5 mr-2 text-primary-600" />
 Contact Details
 </h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
 {selectedCandidate.contact_details.mobile_number && (
 <div>
 <span className="text-slate-500 font-medium">Mobile Number:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.mobile_number}</span>
 </div>
 )}
 {selectedCandidate.contact_details.alternative_mobile_number && (
 <div>
 <span className="text-slate-500 font-medium">Alternative Mobile:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.alternative_mobile_number}</span>
 </div>
 )}
 {selectedCandidate.contact_details.email && (
 <div>
 <span className="text-slate-500 font-medium">Email:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.email}</span>
 </div>
 )}
 {selectedCandidate.contact_details.current_address && (
 <div className="md:col-span-2">
 <span className="text-slate-500 font-medium">Current Address:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.current_address}</span>
 </div>
 )}
 {selectedCandidate.contact_details.permanent_address && (
 <div className="md:col-span-2">
 <span className="text-slate-500 font-medium">Permanent Address:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.permanent_address}</span>
 </div>
 )}
 {selectedCandidate.contact_details.city && (
 <div>
 <span className="text-slate-500 font-medium">City:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.city}</span>
 </div>
 )}
 {selectedCandidate.contact_details.postal_code && (
 <div>
 <span className="text-slate-500 font-medium">Postal Code:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.postal_code}</span>
 </div>
 )}
 {selectedCandidate.contact_details.country && (
 <div>
 <span className="text-slate-500 font-medium">Country:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.country}</span>
 </div>
 )}
 {selectedCandidate.contact_details.state_province && (
 <div>
 <span className="text-slate-500 font-medium">State/Province:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.state_province}</span>
 </div>
 )}
 {selectedCandidate.contact_details.domicile && (
 <div>
 <span className="text-slate-500 font-medium">Domicile:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.contact_details.domicile}</span>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Demographics */}
 {selectedCandidate.demographics && (
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <IdentificationIcon className="h-5 w-5 mr-2 text-primary-600" />
 Demographics
 </h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
 {Object.entries(selectedCandidate.demographics)
 .filter(([key]) => key !== 'profile_picture_url' && key !== 'cnic_bform_image_url')
 .map(([key, value]) => (
 <div key={key}>
 <span className="text-slate-500 font-medium capitalize">{key.replace(/_/g, ' ')}:</span>
 <span className="ml-2 text-slate-900">{value || 'N/A'}</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Guardian Details */}
 {selectedCandidate.guardian_details && (
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <HomeIcon className="h-5 w-5 mr-2 text-primary-600" />
 Guardian Details
 </h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
 {(selectedCandidate.guardian_details.father_first_name || selectedCandidate.guardian_details.father_last_name) && (
 <div>
 <span className="text-slate-500 font-medium">Father's Name:</span>
 <span className="ml-2 text-slate-900">
 {[selectedCandidate.guardian_details.father_first_name, selectedCandidate.guardian_details.father_last_name].filter(Boolean).join(' ')}
 </span>
 </div>
 )}
 {selectedCandidate.guardian_details.father_cnic && (
 <div>
 <span className="text-slate-500 font-medium">Father's CNIC:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.guardian_details.father_cnic}</span>
 </div>
 )}
 {(selectedCandidate.guardian_details.mother_first_name || selectedCandidate.guardian_details.mother_last_name) && (
 <div>
 <span className="text-slate-500 font-medium">Mother's Name:</span>
 <span className="ml-2 text-slate-900">
 {[selectedCandidate.guardian_details.mother_first_name, selectedCandidate.guardian_details.mother_last_name].filter(Boolean).join(' ')}
 </span>
 </div>
 )}
 {selectedCandidate.guardian_details.mother_cnic && (
 <div>
 <span className="text-slate-500 font-medium">Mother's CNIC:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.guardian_details.mother_cnic}</span>
 </div>
 )}
 {selectedCandidate.guardian_details.mother_cell && (
 <div>
 <span className="text-slate-500 font-medium">Mother's Cell:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.guardian_details.mother_cell}</span>
 </div>
 )}
 {selectedCandidate.guardian_details.number_of_siblings !== undefined && selectedCandidate.guardian_details.number_of_siblings !== null && (
 <div>
 <span className="text-slate-500 font-medium">Number of Siblings:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.guardian_details.number_of_siblings}</span>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Program Preferences */}
 {selectedCandidate.program_preferences && selectedCandidate.program_preferences.length > 0 && (
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <AcademicCapIcon className="h-5 w-5 mr-2 text-primary-600" />
 Program Preferences
 </h4>
 <div className="space-y-2">
 {selectedCandidate.program_preferences.map((pref, index) => (
 <div key={index} className="bg-white p-3 rounded border border-slate-200">
 <div className="flex items-start space-x-2">
 <span className="bg-primary-100 text-primary-700 px-2 py-1 rounded text-xs font-medium">
 Preference #{pref.preference_order || index + 1}
 </span>
 <div className="flex-1">
 <div className="text-slate-900 font-medium">
 {pref.degree_type_name || getDegreeTypeName(pref.degree_type_id)} in {pref.field_of_study_name || getFieldOfStudyName(pref.field_of_study_id)}
 </div>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Academic Records */}
 {selectedCandidate.academic_records && selectedCandidate.academic_records.length > 0 && (
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <AcademicCapIcon className="h-5 w-5 mr-2 text-primary-600" />
 Academic Records
 </h4>
 <div className="space-y-3">
 {selectedCandidate.academic_records.map((record, index) => (
 <div key={index} className="bg-white p-4 rounded border border-slate-200">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
 <div>
 <span className="text-slate-500 font-medium">Level:</span>
 <span className="ml-2 text-slate-900">{record.qualification_level}</span>
 </div>
 <div>
 <span className="text-slate-500 font-medium">Institute:</span>
 <span className="ml-2 text-slate-900">{record.institute_name}</span>
 </div>
 {record.board_name && (
 <div>
 <span className="text-slate-500 font-medium">Board:</span>
 <span className="ml-2 text-slate-900">{record.board_name}</span>
 </div>
 )}
 <div>
 <span className="text-slate-500 font-medium">Year:</span>
 <span className="ml-2 text-slate-900">{record.passing_year}</span>
 </div>
 {record.discipline && (
 <div>
 <span className="text-slate-500 font-medium">Discipline:</span>
 <span className="ml-2 text-slate-900">{record.discipline}</span>
 </div>
 )}
 <div>
 <span className="text-slate-500 font-medium">Marks:</span>
 <span className="ml-2 text-slate-900">
 {record.grading_type === 'Percentage' && record.obtained_marks && record.total_marks ? (
 <>
 {record.obtained_marks}/{record.total_marks}
 <span className="font-semibold text-primary-600">
 {' '}({((parseFloat(record.obtained_marks) / parseFloat(record.total_marks)) * 100).toFixed(2)}%)
 </span>
 </>
 ) : record.grading_type === 'CGPA' && record.obtained_marks ? (
 <>CGPA: {record.obtained_marks}/4.0</>
 ) : record.obtained_marks && record.total_marks ? (
 <>{record.obtained_marks}/{record.total_marks}</>
 ) : (
 'Not provided'
 )}
 {record.grading_type && ` (${record.grading_type})`}
 </span>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Documents */}
 {selectedCandidate.documents && Object.keys(selectedCandidate.documents).length > 0 && (
 <div className="bg-slate-50 p-4 rounded-lg">
 <h4 className="font-semibold text-slate-900 mb-3 flex items-center">
 <svg className="h-5 w-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
 </svg>
 Documents
 </h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
 {selectedCandidate.documents.cnic_bform_front_url && (
 <div>
 <span className="text-slate-500 font-medium">CNIC/B-Form (Front):</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.cnic_bform_front_url, title: 'CNIC/B-Form (Front)' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.cnic_bform_back_url && (
 <div>
 <span className="text-slate-500 font-medium">CNIC/B-Form (Back):</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.cnic_bform_back_url, title: 'CNIC/B-Form (Back)' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.ssc_marksheet_url && (
 <div>
 <span className="text-slate-500 font-medium">SSC Marksheet:</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.ssc_marksheet_url, title: 'SSC Marksheet' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.ssc_certificate_url && (
 <div>
 <span className="text-slate-500 font-medium">SSC Certificate:</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.ssc_certificate_url, title: 'SSC Certificate' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.hsc_marksheet_url && (
 <div>
 <span className="text-slate-500 font-medium">HSC Marksheet:</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.hsc_marksheet_url, title: 'HSC Marksheet' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.hsc_certificate_url && (
 <div>
 <span className="text-slate-500 font-medium">HSC Certificate:</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.hsc_certificate_url, title: 'HSC Certificate' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.domicile_certificate_url && (
 <div>
 <span className="text-slate-500 font-medium">Domicile Certificate:</span>
 <button
 type="button"
 onClick={() => setDocViewer({ url: selectedCandidate.documents.domicile_certificate_url, title: 'Domicile Certificate' })}
 className="ml-2 text-primary-600 hover:text-primary-800 underline"
 >
 View Document
 </button>
 </div>
 )}
 {selectedCandidate.documents.extra_activities_certificates && (
 <div className="md:col-span-2">
 <span className="text-slate-500 font-medium">Extra Activities:</span>
 <span className="ml-2 text-slate-900">{selectedCandidate.documents.extra_activities_certificates}</span>
 </div>
 )}
 </div>
 </div>
 )}
 </div>

 <div className="mt-6 pt-4 border-t flex justify-end">
 <button
 onClick={() => setShowCandidateModal(false)}
 className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
 >
 Close
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Document viewer modal */}
 {docViewer.url && (
 <DocumentViewerModal
 url={docViewer.url}
 title={docViewer.title}
 onClose={() => setDocViewer({ url: '', title: '' })}
 />
 )}

 {/* Rejection Reason Modal */}
 {showRejectionModal && (
 <div className="fixed inset-0 bg-slate-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
 <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
 <div className="mt-3">
 <h3 className="text-lg font-medium text-slate-900 mb-4">
 Reject Application
 </h3>
 <p className="text-sm text-slate-600 mb-4">
 Please provide a reason for rejecting this application. This will be visible to the student.
 </p>
 <textarea
 value={rejectionReason}
 onChange={(e) => setRejectionReason(e.target.value)}
 placeholder="Enter rejection reason..."
 className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
 rows="4"
 maxLength="500"
 />
 <div className="text-right text-xs text-slate-500 mt-1">
 {rejectionReason.length}/500 characters
 </div>
 <div className="flex justify-end space-x-3 mt-6">
 <button
 onClick={handleRejectCancel}
 className="px-4 py-2 bg-slate-200 text-slate-800 rounded-md hover:bg-slate-300 transition-colors"
 >
 Cancel
 </button>
 <button
 onClick={handleRejectConfirm}
 disabled={!rejectionReason.trim() || updatingStatus}
 className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >
 {updatingStatus ? 'Rejecting...' : 'Reject Application'}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}));

const StudentProfile = React.memo(({
 profileData,
 user,
 uploading,
 profilePictureRef,
 isEditing,
 editForm,
 editValidationErrors,
 savingProfile,
 cities,
 fieldsOfStudy,
 handleStartEdit,
 handleCancelEdit,
 handleSaveStudentProfile,
 handleEditFormChange,
 handleImageUpload,
 handleRemoveStudentProfilePicture
}) => (
 <div className="space-y-6">
 {/* Profile Picture Section */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex flex-col sm:flex-row items-center sm:space-x-6 gap-4 sm:gap-0 text-center sm:text-left">
 <div className="relative">
 {profileData?.profile_picture ? (
 <img
 src={profileData.profile_picture}
 alt="Profile"
 className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-md shadow-slate-200/50"
 />
 ) : (
 <div className="w-32 h-32 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shadow-inner">
 <UserCircleIcon className="w-20 h-20 text-slate-400" />
 </div>
 )}
 <button
 onClick={() => profilePictureRef.current?.click()}
 disabled={uploading}
 className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2.5 rounded-xl shadow-md shadow-primary-500/10 hover:shadow-lg hover:shadow-primary-500/20 active:scale-95 transition-all disabled:opacity-50"
 >
 <PencilIcon className="w-4 h-4" />
 </button>
 <input
 ref={profilePictureRef}
 type="file"
 accept="image/*"
 onChange={(e) => handleImageUpload(e.target.files[0], 'profile')}
 className="hidden"
 />
 </div>
 <div className="flex-1">
 <h3 className="text-xl font-bold text-slate-800">{profileData?.name || user?.name}</h3>
 <p className="text-sm font-semibold text-slate-500 mt-0.5">Student Account</p>
 <p className="text-xs text-slate-400 mt-2 max-w-md">
 Upload a high-quality profile picture to personalize your applications (180x180px recommended).
 </p>
 {profileData?.profile_picture && (
 <button
 onClick={handleRemoveStudentProfilePicture}
 disabled={uploading}
 className="mt-3 inline-flex items-center space-x-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 transition-colors disabled:opacity-50"
 >
 <TrashIcon className="w-3.5 h-3.5" />
 <span>Remove picture</span>
 </button>
 )}
 </div>
 </div>
 </div>

 {/* Personal Information */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
 <div className="flex items-center space-x-2.5">
 <UserCircleIcon className="h-6 w-6 text-primary-600" />
 <h3 className="text-lg font-bold text-slate-800">{isEditing ? 'Editing Profile Details' : 'Personal Information'}</h3>
 </div>
 {!isEditing ? (
 <button
 onClick={handleStartEdit}
 className="btn-secondary text-xs px-3.5 py-2 font-bold shadow-sm flex items-center gap-1.5"
 >
 <PencilIcon className="w-3.5 h-3.5" />
 <span>Edit Profile</span>
 </button>
 ) : (
 <div className="flex items-center gap-2">
 <button
 onClick={handleCancelEdit}
 disabled={savingProfile}
 className="btn-secondary text-xs px-3.5 py-2 font-bold shadow-sm disabled:opacity-50"
 >
 Cancel
 </button>
 <button
 onClick={handleSaveStudentProfile}
 disabled={savingProfile}
 className="btn-primary text-xs px-4 py-2 font-bold shadow-sm disabled:opacity-50 flex items-center gap-1"
 >
 {savingProfile ? (
 <>
 <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
 </svg>
 <span>Saving...</span>
 </>
 ) : (
 'Save Changes'
 )}
 </button>
 </div>
 )}
 </div>

 {isEditing ? (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Full Name</label>
 <input
 type="text"
 value={editForm.name}
 onChange={(e) => handleEditFormChange('name', e.target.value)}
 className={`w-full px-3.5 py-2 bg-white border rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm ${editValidationErrors.name ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
 placeholder="Enter full name"
 />
 {editValidationErrors.name && (
 <p className="text-red-600 text-xs mt-1 font-semibold">{editValidationErrors.name}</p>
 )}
 </div>

 <div>
 <label className="text-xs font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Email Address</label>
 <input
 type="email"
 value={profileData?.email || user?.email || ''}
 disabled
 className="w-full px-3.5 py-2 border border-slate-200 rounded-xl bg-slate-100 text-slate-400 font-semibold text-sm cursor-not-allowed"
 />
 </div>

 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Phone Number</label>
 <input
 type="tel"
 value={editForm.phone}
 onChange={(e) => handleEditFormChange('phone', e.target.value)}
 className={`w-full px-3.5 py-2 bg-white border rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm ${editValidationErrors.phone ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
 placeholder="03XXXXXXXXX"
 maxLength="11"
 />
 {editValidationErrors.phone ? (
 <p className="text-red-600 text-xs mt-1 font-semibold">{editValidationErrors.phone}</p>
 ) : (
 <p className="text-slate-400 text-[10px] mt-1 font-medium">11-digit format (03XXXXXXXXX)</p>
 )}
 </div>

 <div>
 <label className="text-xs font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Date of Birth</label>
 <input
 type="text"
 value={profileData?.date_of_birth ? new Date(profileData.date_of_birth).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not provided'}
 disabled
 className="w-full px-3.5 py-2 border border-slate-200 rounded-xl bg-slate-100 text-slate-400 font-semibold text-sm cursor-not-allowed"
 />
 </div>

 <div>
 <label className="text-xs font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Gender</label>
 <input
 type="text"
 value={profileData?.gender || 'Not provided'}
 disabled
 className="w-full px-3.5 py-2 border border-slate-200 rounded-xl bg-slate-100 text-slate-400 font-semibold text-sm cursor-not-allowed"
 />
 </div>

 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Discipline</label>
 <select
 value={editForm.discipline}
 onChange={(e) => handleEditFormChange('discipline', e.target.value)}
 className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm cursor-pointer"
 >
 <option value="">Select Discipline</option>
 <option value="Pre-Medical">Pre-Medical</option>
 <option value="Pre-Engineering">Pre-Engineering</option>
 <option value="Computer Science">Computer Science</option>
 <option value="General Science">General Science</option>
 <option value="Humanities">Humanities</option>
 <option value="Commerce">Commerce</option>
 <option value="Home Economics">Home Economics</option>
 <option value="Pre-Agricultural">Pre-Agricultural</option>
 <option value="Para-Medical">Para-Medical</option>
 </select>
 </div>

 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Intermediate Marks (%)</label>
 <input
 type="number"
 min="0"
 max="100"
 step="0.01"
 value={editForm.intermediate_marks}
 onChange={(e) => handleEditFormChange('intermediate_marks', e.target.value)}
 className={`w-full px-3.5 py-2 bg-white border rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm ${editValidationErrors.intermediate_marks ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
 placeholder="e.g. 85.5"
 />
 {editValidationErrors.intermediate_marks && (
 <p className="text-red-600 text-xs mt-1 font-semibold">{editValidationErrors.intermediate_marks}</p>
 )}
 </div>

 <div className="md:col-span-2">
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Preferred Field of Study</label>
 <select
 value={editForm.field_of_interest_id}
 onChange={(e) => handleEditFormChange('field_of_interest_id', e.target.value)}
 className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm cursor-pointer"
 >
 <option value="">Select Field of Study</option>
 {fieldsOfStudy.map(field => (
 <option key={field.id} value={field.id}>{field.name}</option>
 ))}
 </select>
 </div>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Full Name</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.name || user?.name || 'Not provided'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Email Address</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm truncate max-w-xs">{profileData?.email || user?.email || 'Not provided'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Phone Number</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.phone || 'Not provided'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Date of Birth</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">
 {profileData?.date_of_birth
 ? new Date(profileData.date_of_birth).toLocaleDateString('en-US', {
 year: 'numeric',
 month: 'long',
 day: 'numeric'
 })
 : 'Not provided'
 }
 </p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gender</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.gender || 'Not provided'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Discipline</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.discipline || 'Not specified'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Intermediate Marks</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">
 {profileData?.intermediate_marks ? `${profileData.intermediate_marks}%` : 'Not provided'}
 </p>
 </div>
 <div className="md:col-span-2">
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Preferred Field of Study</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.field_of_interest || profileData?.preferred_field || 'Not specified'}</p>
 </div>
 </div>
 )}
 </div>

 {/* Address Information */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
 <div className="flex items-center space-x-2.5 mb-6 border-b border-slate-100 pb-4">
 <HomeIcon className="h-6 w-6 text-primary-600" />
 <h3 className="text-lg font-bold text-slate-800">Address Information</h3>
 </div>

 {isEditing ? (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
 {/* Address Line */}
 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Address</label>
 <input
 type="text"
 value={editForm.address_line}
 onChange={(e) => handleEditFormChange('address_line', e.target.value)}
 className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm"
 placeholder="Enter address"
 />
 </div>

 {/* City */}
 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">City</label>
 <select
 value={editForm.city_id}
 onChange={(e) => handleEditFormChange('city_id', e.target.value)}
 className="w-full px-3.5 py-2 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm cursor-pointer"
 >
 <option value="">Select City</option>
 {cities.map(city => (
 <option key={city.id} value={city.id}>{city.name}</option>
 ))}
 </select>
 </div>

 {/* Postal Code */}
 <div>
 <label className="text-xs font-bold text-slate-700 mb-1.5 block uppercase tracking-wider">Postal Code</label>
 <input
 type="text"
 value={editForm.postal_code}
 onChange={(e) => {
 const value = e.target.value.replace(/\D/g, '').slice(0, 5);
 handleEditFormChange('postal_code', value);
 }}
 className={`w-full px-3.5 py-2 bg-white border rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-premium text-sm ${editValidationErrors.postal_code ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}
 placeholder="e.g. 54000"
 maxLength="5"
 />
 {editValidationErrors.postal_code && (
 <p className="text-red-600 text-xs mt-1 font-semibold">{editValidationErrors.postal_code}</p>
 )}
 </div>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Address</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.address_line || 'Not provided'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">City</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.city || profileData?.lives_in || 'Not provided'}</p>
 </div>
 <div>
 <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Postal Code</p>
 <p className="font-semibold text-slate-800 mt-0.5 text-sm">{profileData?.postal_code || 'Not provided'}</p>
 </div>
 </div>
 )}
 </div>

 {isEditing && (
 <div className="flex justify-end gap-3 mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm p-4">
 <button
 onClick={handleCancelEdit}
 disabled={savingProfile}
 className="btn-secondary text-sm px-5 py-2.5 font-bold shadow-sm disabled:opacity-50"
 >
 Cancel
 </button>
 <button
 onClick={handleSaveStudentProfile}
 disabled={savingProfile}
 className="btn-primary text-sm px-6 py-2.5 font-bold shadow-sm disabled:opacity-50 flex items-center gap-2"
 >
 {savingProfile ? (
 <>
 <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
 </svg>
 <span>Saving Changes...</span>
 </>
 ) : (
 'Save Profile Changes'
 )}
 </button>
 </div>
 )}
 </div>
));

const Profile = () => {
 const { user, isAuthenticated, isStudent, isUniversity, updateUser } = useAuth();
 const navigate = useNavigate();
 const location = useLocation();
 const [activeTab, setActiveTab] = useState('profile');
 const [profileData, setProfileData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [uploading, setUploading] = useState(false);
 const [newProgram, setNewProgram] = useState('');
 const [addingProgram, setAddingProgram] = useState(false);
 const [aboutMe, setAboutMe] = useState('');
 const [websiteLink, setWebsiteLink] = useState('');
 const [savingProfile, setSavingProfile] = useState(false);
 const [notification, setNotification] = useState({ message: '', type: '' });
 const [isEditingAbout, setIsEditingAbout] = useState(false);
 const [editingProgram, setEditingProgram] = useState(null);
 const [savingProgram, setSavingProgram] = useState(false);
 const [degreeTypes, setDegreeTypes] = useState([]);
 const [fieldsOfStudy, setFieldsOfStudy] = useState([]);
 const [cities, setCities] = useState([]);
 const [isEditing, setIsEditing] = useState(false);
 const [editForm, setEditForm] = useState({
 name: '',
 phone: '',
 address_line: '',
 city_id: '',
 postal_code: '',
 discipline: '',
 field_of_interest_id: '',
 intermediate_marks: ''
 });
 const [editValidationErrors, setEditValidationErrors] = useState({});

 const handleStartEdit = () => {
 setEditForm({
 name: profileData?.name || '',
 phone: profileData?.phone || '',
 address_line: profileData?.address_line || '',
 city_id: profileData?.city_id || '',
 postal_code: profileData?.postal_code || '',
 discipline: profileData?.discipline || '',
 field_of_interest_id: profileData?.field_of_interest_id || '',
 intermediate_marks: profileData?.intermediate_marks || ''
 });
 setEditValidationErrors({});
 setIsEditing(true);
 };

 const handleCancelEdit = () => {
 setIsEditing(false);
 setEditValidationErrors({});
 };

 const handleEditFormChange = (field, value) => {
 setEditForm(prev => ({
 ...prev,
 [field]: value
 }));
 if (editValidationErrors[field]) {
 setEditValidationErrors(prev => {
 const copy = { ...prev };
 delete copy[field];
 return copy;
 });
 }
 };

 const handleSaveStudentProfile = async () => {
 const errors = {};
 if (!editForm.name || !editForm.name.trim()) {
 errors.name = 'Full name is required';
 }
 if (editForm.phone) {
 const cleanPhone = editForm.phone.replace(/\s/g, '');
 if (!/^03\d{9}$/.test(cleanPhone)) {
 errors.phone = 'Phone number must be in Pakistani format (03XXXXXXXXX)';
 }
 }
 if (editForm.postal_code) {
 if (!/^\d{5}$/.test(editForm.postal_code)) {
 errors.postal_code = 'Postal code must be exactly 5 digits';
 }
 }
 if (editForm.intermediate_marks !== null && editForm.intermediate_marks !== '') {
 const marks = parseFloat(editForm.intermediate_marks);
 if (isNaN(marks) || marks < 0 || marks > 100) {
 errors.intermediate_marks = 'Marks must be between 0 and 100';
 }
 }

 if (Object.keys(errors).length > 0) {
 setEditValidationErrors(errors);
 showNotification('Please fix the validation errors before saving.', 'error');
 return;
 }

 setSavingProfile(true);
 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

 const payload = {
 name: editForm.name.trim(),
 phone: editForm.phone || null,
 address_line: editForm.address_line || null,
 city_id: editForm.city_id ? parseInt(editForm.city_id, 10) : null,
 postal_code: editForm.postal_code || null,
 discipline: editForm.discipline || null,
 field_of_interest_id: editForm.field_of_interest_id ? parseInt(editForm.field_of_interest_id, 10) : null,
 intermediate_marks: editForm.intermediate_marks !== '' ? parseFloat(editForm.intermediate_marks) : null
 };

 const response = await fetch(`${apiUrl}/student/profile`, {
 method: 'PUT',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify(payload)
 });

 if (response.ok) {
 showNotification('Profile updated successfully!', 'success');
 setIsEditing(false);
 fetchProfileData(); // Reload profile data
 } else {
 const errorData = await response.json();
 showNotification(errorData.error || 'Failed to update profile.', 'error');
 }
 } catch (err) {
 console.error('Error saving profile:', err);
 showNotification('Failed to update profile due to a network error.', 'error');
 } finally {
 setSavingProfile(false);
 }
 };

 const profilePictureRef = useRef(null);
 const coverPhotoRef = useRef(null);
 const programsSectionRef = useRef(null);
 const applicantsSectionRef = useRef(null);
 const faqsSectionRef = useRef(null);
 const feesSectionRef = useRef(null);

 // Check for tab query parameter
 useEffect(() => {
 const params = new URLSearchParams(location.search);
 const tab = params.get('tab');
 if (tab && (tab === 'applications' || tab === 'programs' || tab === 'applicants' || tab === 'faqs' || tab === 'fees')) {
 setActiveTab(tab);
 }
 }, [location.search]);

 // Helper function to show notifications
 const showNotification = (message, type) => {
 setNotification({ message, type });
 setTimeout(() => setNotification({ message: '', type: '' }), 5000);
 };

 // Fetch dropdown lists (degree types, fields of study, cities)
 useEffect(() => {
 const fetchDropdownData = async () => {
 try {
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

 // Fetch fields of study for both
 const fieldsPromise = fetch(API_ENDPOINTS.FIELDS_OF_STUDY);
 // Fetch cities for both
 const citiesPromise = fetch(`${apiUrl}/cities`);
 // Fetch degree types only for university
 const degreeTypesPromise = isUniversity ? fetch(API_ENDPOINTS.DEGREE_TYPES) : null;

 const [fieldsRes, citiesRes, degreeTypesRes] = await Promise.all(
 [fieldsPromise, citiesPromise, degreeTypesPromise].filter(Boolean)
 );

 if (fieldsRes.ok) {
 const fieldsData = await fieldsRes.json();
 setFieldsOfStudy(fieldsData);
 }

 if (citiesRes.ok) {
 const citiesData = await citiesRes.json();
 setCities(citiesData);
 }

 if (degreeTypesRes && degreeTypesRes.ok) {
 const degreeTypesData = await degreeTypesRes.json();
 setDegreeTypes(degreeTypesData);
 }
 } catch (error) {
 console.error('Error fetching dropdown data:', error);
 }
 };

 if (isStudent || isUniversity) {
 fetchDropdownData();
 }
 }, [isStudent, isUniversity]);
 const fetchProfileData = useCallback(async () => {
 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const endpoint = isStudent
 ? `${apiUrl}/student/profile`
 : `${apiUrl}/university/profile`;

 const response = await fetch(endpoint, {
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 });

 if (response.ok) {
 const data = await response.json();
 setProfileData(data);
 if (data.profile_picture !== user?.profile_picture || data.name !== user?.name) {
 updateUser({
 profile_picture: data.profile_picture,
 name: data.name
 });
 }

 // Initialize About Me fields for universities
 if (data.role === 'university') {
 setAboutMe(data.about_me || data.about_university || data.campus_info || '');
 setWebsiteLink(data.website_link || data.website_url || '');
 }
 } else {
 const errorData = await response.json();
 console.error('Profile fetch error:', errorData);
 }
 } catch (error) {
 console.error('Error fetching profile:', error);
 } finally {
 setLoading(false);
 }
 }, [isStudent]); // Added isStudent dependency

 useEffect(() => {
 document.title = 'Profile - Dakhla';
 }, []);

 useEffect(() => {
 if (!isAuthenticated) {
 navigate('/login');
 return;
 }
 fetchProfileData();
 }, [isAuthenticated, navigate, fetchProfileData]);

 const resizeImage = (file, maxWidth, maxHeight) => {
 return new Promise((resolve) => {
 const canvas = document.createElement('canvas');
 const ctx = canvas.getContext('2d');
 const img = new Image();

 img.onload = () => {
 canvas.width = maxWidth;
 canvas.height = maxHeight;
 ctx.drawImage(img, 0, 0, maxWidth, maxHeight);
 resolve(canvas.toDataURL('image/jpeg', 0.8));
 };

 img.src = URL.createObjectURL(file);
 });
 };

 const handleImageUpload = async (file, type) => {
 if (!file) return;

 setUploading(true);
 try {
 let resizedImage;
 if (type === 'profile') {
 resizedImage = await resizeImage(file, 180, 180);
 } else if (type === 'cover') {
 resizedImage = await resizeImage(file, 820, 312);
 }

 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 let endpoint;
 if (isStudent) {
 endpoint = `${apiUrl}/student/upload-profile-picture`;
 } else {
 endpoint = type === 'profile'
 ? `${apiUrl}/university/profile-picture`
 : `${apiUrl}/university/cover-photo`;
 }

 const token = localStorage.getItem('token');
 const response = await fetch(endpoint, {
 method: 'POST',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({ image: resizedImage })
 });

 if (response.ok) {
 // Refresh profile data
 fetchProfileData();
 } else {
 const errorData = await response.json();
 console.error('Upload error:', errorData);
 }
 } catch (error) {
 console.error('Error uploading image:', error);
 } finally {
 setUploading(false);
 }
 };

 const handleAddProgram = useCallback(async (programData) => {
 // Handle both old format (string) and new format (object)
 const dataToSend = typeof programData === 'string'
 ? { program_name: programData.trim() }
 : {
 program_name: programData.program_name?.trim(),
 program_fee: programData.program_fee ? parseFloat(programData.program_fee) : null,
 application_deadline: programData.application_deadline || null,
 duration: programData.duration || null,
 degree_type: programData.degree_type || null,
 description: programData.description?.trim() || null,
 last_year_cutoff_min: programData.last_year_cutoff_min ? parseFloat(programData.last_year_cutoff_min) : null,
 last_year_cutoff_max: programData.last_year_cutoff_max ? parseFloat(programData.last_year_cutoff_max) : null
 };

 if (!dataToSend.program_name) return;

 setAddingProgram(true);
 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university/programs`, {
 method: 'POST',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify(dataToSend)
 });

 if (response.ok) {
 setNewProgram('');
 fetchProfileData(); // Refresh to show new program
 } else {
 const errorData = await response.json();
 console.error('Add program error:', errorData);
 showNotification(errorData.error || 'Failed to add program', 'error');
 }
 } catch (error) {
 console.error('Error adding program:', error);
 showNotification('Failed to add program. Please try again.', 'error');
 } finally {
 setAddingProgram(false);
 }
 }, []);

 const handleEditProgram = useCallback(async (programId, programData) => {
 setSavingProgram(true);
 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university/programs/${programId}`, {
 method: 'PUT',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify(programData)
 });

 if (response.ok) {
 await response.json();
 setEditingProgram(null);
 fetchProfileData(); // Refresh to show updated program
 showNotification('Program updated successfully!', 'success');
 } else {
 const errorData = await response.json();
 console.error('Edit program error:', errorData);
 showNotification(errorData.error || 'Failed to update program', 'error');
 }
 } catch (error) {
 console.error('Error editing program:', error);
 showNotification('Failed to update program. Please try again.', 'error');
 } finally {
 setSavingProgram(false);
 }
 }, [fetchProfileData]);

 const handleDeleteProgram = useCallback(async (programId) => {
 if (!window.confirm('Are you sure you want to delete this program?')) {
 return;
 }

 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university/programs/${programId}`, {
 method: 'DELETE',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 });

 if (response.ok) {
 fetchProfileData(); // Refresh to remove deleted program
 showNotification('Program deleted successfully!', 'success');
 } else {
 const errorData = await response.json();
 console.error('Delete program error:', errorData);
 showNotification(errorData.error || 'Failed to delete program', 'error');
 }
 } catch (error) {
 console.error('Error deleting program:', error);
 showNotification('Failed to delete program. Please try again.', 'error');
 }
 }, [fetchProfileData]);

 const handleProgramInputChange = useCallback((e) => {
 setNewProgram(e.target.value);
 }, []);

 const handleSaveProfile = useCallback(async () => {
 setSavingProfile(true);
 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/university/profile`, {
 method: 'PUT',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 },
 body: JSON.stringify({
 about_me: aboutMe,
 website_link: websiteLink
 })
 });

 if (response.ok) {
 // Refresh profile data to show updated information
 fetchProfileData();
 showNotification('Profile updated successfully!', 'success');
 } else {
 const errorData = await response.json();
 console.error('Save profile error:', errorData);
 showNotification(errorData.error || 'Failed to save profile', 'error');
 }
 } catch (error) {
 console.error('Error saving profile:', error);
 showNotification('Failed to save profile. Please try again.', 'error');
 } finally {
 setSavingProfile(false);
 }
 }, [aboutMe, websiteLink, fetchProfileData]);

 const handleTabClick = useCallback((tabId) => {
 setActiveTab(tabId);
 }, []);

 if (loading) {
 return <div className="flex justify-center items-center h-64">Loading...</div>;
 }

 const handleRemoveStudentProfilePicture = async () => {
 if (!window.confirm('Remove your profile picture?')) return;
 setUploading(true);
 try {
 const token = localStorage.getItem('token');
 const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
 const response = await fetch(`${apiUrl}/student/profile-picture`, {
 method: 'DELETE',
 headers: { 'Authorization': `Bearer ${token}` }
 });
 if (response.ok) {
 setProfileData(prev => ({ ...prev, profile_picture: null }));
 updateUser({ profile_picture: null });
 showNotification('Profile picture removed.', 'success');
 } else {
 showNotification('Failed to remove profile picture.', 'error');
 }
 } catch (err) {
 console.error('Error saving profile:', err);
 showNotification('Failed to update profile due to a network error.', 'error');
 } finally {
 setSavingProfile(false);
 }
 };



 const tabs = isStudent
 ? [
 { id: 'profile', name: 'Profile', icon: UserCircleIcon },
 { id: 'applications', name: 'My Applications', icon: AcademicCapIcon }
 ]
 : [
 { id: 'profile', name: 'University Info', icon: BuildingOfficeIcon },
 { id: 'programs', name: 'Programs', icon: AcademicCapIcon },
 { id: 'fees', name: 'Fee Structure', icon: CurrencyDollarIcon },
 { id: 'applicants', name: 'Applicants', icon: UserCircleIcon },
 { id: 'admission-questions', name: 'Admission Questions', icon: QuestionMarkCircleIcon },
 { id: 'faqs', name: 'FAQs', icon: QuestionMarkCircleIcon }
 ];

 return (
 <div className="min-h-screen bg-slate-100 py-4 sm:py-8">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

 {/* Notification */}
 {notification.message && (
 <div className={`mb-4 sm:mb-6 p-4 rounded-xl text-sm sm:text-base border transition-premium ${notification.type === 'success'
 ? 'bg-green-50/50 border-green-200 text-green-700 shadow-sm shadow-green-500/5'
 : 'bg-red-50/50 border-red-200 text-red-700 shadow-sm shadow-red-500/5'
 }`}>
 <span className="flex items-center gap-2">
 <span className="text-base">{notification.type === 'success' ? '✅' : '⚠️'}</span>
 <span className="font-medium">{notification.message}</span>
 </span>
 </div>
 )}

 {isStudent ? (
 <StudentProfile
 profileData={profileData}
 user={user}
 uploading={uploading}
 profilePictureRef={profilePictureRef}
 isEditing={isEditing}
 editForm={editForm}
 editValidationErrors={editValidationErrors}
 savingProfile={savingProfile}
 cities={cities}
 fieldsOfStudy={fieldsOfStudy}
 handleStartEdit={handleStartEdit}
 handleCancelEdit={handleCancelEdit}
 handleSaveStudentProfile={handleSaveStudentProfile}
 handleEditFormChange={handleEditFormChange}
 handleImageUpload={handleImageUpload}
 handleRemoveStudentProfilePicture={handleRemoveStudentProfilePicture}
 />
 ) : (
 <>

 {/* Navigation Tabs */}
 <div className="card bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden">
 <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto scrollbar-hide border-b border-slate-100">
 {tabs.map((tab) => {
 const Icon = tab.icon;
 return (
 <button
 key={tab.id}
 onClick={() => handleTabClick(tab.id)}
 className={`py-4 px-2 sm:px-1 border-b-2 font-semibold text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap flex-shrink-0 transition-all duration-300 ${activeTab === tab.id
 ? 'border-primary-600 text-primary-600 scale-[1.02]'
 : 'border-transparent text-slate-500 hover:text-primary-500 hover:border-slate-200'
 }`}
 >
 <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
 <span>{tab.name}</span>
 </button>
 );
 })}
 </nav>
 </div>

 {/* Content */}
 <div>
 {activeTab === 'profile' && (
 <UniversityProfile
 profileData={profileData}
 user={user}
 uploading={uploading}
 profilePictureRef={profilePictureRef}
 coverPhotoRef={coverPhotoRef}
 handleImageUpload={handleImageUpload}
 newProgram={newProgram}
 onProgramChange={handleProgramInputChange}
 handleAddProgram={handleAddProgram}
 addingProgram={addingProgram}
 handleDeleteProgram={handleDeleteProgram}
 aboutMe={aboutMe}
 setAboutMe={setAboutMe}
 websiteLink={websiteLink}
 setWebsiteLink={setWebsiteLink}
 handleSaveProfile={handleSaveProfile}
 savingProfile={savingProfile}
 isEditingAbout={isEditingAbout}
 setIsEditingAbout={setIsEditingAbout}
 degreeTypes={degreeTypes}
 fieldsOfStudy={fieldsOfStudy}
 />
 )}
 {activeTab === 'programs' && <UniversityPrograms
 ref={programsSectionRef}
 profileData={profileData}
 newProgram={newProgram}
 onProgramChange={handleProgramInputChange}
 handleAddProgram={handleAddProgram}
 addingProgram={addingProgram}
 handleDeleteProgram={handleDeleteProgram}
 handleEditProgram={handleEditProgram}
 editingProgram={editingProgram}
 setEditingProgram={setEditingProgram}
 savingProgram={savingProgram}
 degreeTypes={degreeTypes}
 fieldsOfStudy={fieldsOfStudy}
 />}
 {activeTab === 'applicants' && <ApplicationManagement />}
 {activeTab === 'admission-questions' && <AdditionalAdmissionQuestions />}
 {activeTab === 'faqs' && <UniversityFAQManagement ref={faqsSectionRef} />}
 {activeTab === 'fees' && <UniversityFeeStructureManagement ref={feesSectionRef} />}
 </div>
 </>
 )}
 </div>

 {/* Edit Program Modal */}
 {editingProgram && (
 <EditProgramModal
 program={editingProgram}
 onClose={() => setEditingProgram(null)}
 onSave={handleEditProgram}
 saving={savingProgram}
 degreeTypes={degreeTypes}
 fieldsOfStudy={fieldsOfStudy}
 />
 )}
 </div>
 );
};

export default Profile;




